import { Injectable, inject } from '@angular/core';
import { concat, merge, of, type Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {
  DesignPackageSpreadCategoryEnum,
  orderDesignPackSpreads,
  type DesignPackage,
  type DesignPackageSpread,
} from '@lite-workspace/dto-design-system';
import {
  FONT_REGISTRATION_POLL_ATTEMPTS,
  FONT_REGISTRATION_POLL_DELAY_MS,
  FontAutoResolveService,
  FontMappingService,
  FontsTableService,
  isFaceKeptByWeightedMapping,
  templateFontMappingKeys,
  type FontApiResponse,
  type TemplateFontFace,
} from '@lite-workspace/fonts';
import { spreadKindOf } from '@lite-workspace/dto-fotoclass-templates';
import type { DeleteDesignOptions, DesignsDataPort, PackSpreadRef, SpreadKindPick } from './designs.data';
import { DesignsApiService } from './designs-api.service';
import { PsdUploadService, type CreatedSpread } from './psd-upload.service';
import { FontUploadService, type UploadedFont } from './font-upload.service';
import { DesignParseWatcher, type SpreadParseResult } from './design-parse-watcher.service';
import { DesignPreviewWatcher } from './design-preview-watcher.service';
import { readAuthUserId } from '../../../../core/auth-identity';
import { hasCyrillicText } from './font-script.util';
import { previewVersionsSrc, spreadLargeSrc, spreadPreviewSize, spreadPreviewSrc, spreadThumbSrc } from './spread-preview.util';
import type {
  CatalogFontOption,
  DesignPackStatus,
  DesignSummary,
  DesignsPage,
  FontMatchResult,
  FontPickOption,
  FontResolveRow,
  ReviewStatus,
  TemplateParseUpdate,
  TemplatePatch,
  TemplateUpdatesView,
  UploadedTemplate,
} from '../entities/design.models';
import { DesignStatus, templateTypeFromCategory, uploadedFileStateFromSpread, visitSpreadFonts } from '../entities/design.models';

/**
 * Real-backend adapter for the design wizard. Talks to the design-package + spread endpoints on the
 * staging gateway (via DesignsApiService — a verbatim port of the legacy fc-designs CRUD) and maps
 * the DTOs onto the app-native view models the store/components consume. Upload, fonts and review are
 * wired in later phases; until then their port methods surface a clear "not wired yet" error rather
 * than silently no-op.
 */
@Injectable({ providedIn: 'root' })
export class HttpDesignsData implements DesignsDataPort {
  private readonly api = inject(DesignsApiService);
  private readonly psdUpload = inject(PsdUploadService);
  private readonly parseWatcher = inject(DesignParseWatcher);
  private readonly previewWatcher = inject(DesignPreviewWatcher);
  private readonly fontsTable = inject(FontsTableService);
  private readonly fontMapping = inject(FontMappingService);
  private readonly fontUpload = inject(FontUploadService);
  private readonly fontAutoResolve = inject(FontAutoResolveService);
  private readonly ignoreTails = new Map<string, Promise<void>>();

  async getDesignsPage(page: number, limit: number): Promise<DesignsPage> {
    const response = await this.api.loadDesignPackages(limit, page);
    return { designs: response.items.map(toSummary), total: response.count };
  }

  async getDesignById(designId: string): Promise<DesignSummary | null> {
    const pkg = await this.api.loadEnrichedDesignPackage(designId);
    return pkg ? toSummary(pkg) : null;
  }

  async createDesign(name: string): Promise<string> {
    const created = await this.api.createDesignPackage(name);
    return created.id;
  }

  async renameDesign(designId: string, name: string): Promise<void> {
    await this.api.updateDesignPackage(designId, { name });
  }

  // The PATCH replaces printSettings whole, and the list read strips it — the by-id row holds the zones to keep.
  async setPrintProduct(designId: string, printProductId: string): Promise<void> {
    const { printSettings } = await this.api.loadDesignPackage(designId);
    await this.api.updateDesignPackage(designId, { printSettings: { ...printSettings, printProductId } });
  }

  async deleteDesign(designId: string, options?: DeleteDesignOptions): Promise<void> {
    await this.api.deleteDesignPackage(designId, options);
  }

  async getTemplates(designId: string): Promise<UploadedTemplate[]> {
    const spreads = await this.api.loadSpreads(designId);
    return orderDesignPackSpreads(photographerSpreads(spreads)).map(toTemplate);
  }

  async removeTemplate(designId: string, templateId: string): Promise<void> {
    await this.api.deleteSpread(designId, templateId);
  }

  // PATCH replaces the whole model: a fresh read keeps the parse/markup, and writes to one spread queue so
  // two quick ignores cannot both read the same list and drop each other's key.
  ignoreSpreadChecks(designId: string, spreadId: string, keys: readonly string[]): Promise<void> {
    return this.editIgnoredChecks(designId, spreadId, current => [...new Set([...current, ...keys])]);
  }

  removeSpreadChecks(designId: string, spreadId: string, keys: readonly string[]): Promise<void> {
    return this.editIgnoredChecks(designId, spreadId, current => current.filter(key => !keys.includes(key)));
  }

  private editIgnoredChecks(designId: string, spreadId: string, edit: (current: readonly string[]) => string[]): Promise<void> {
    const write = (this.ignoreTails.get(spreadId) ?? Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        const { model } = await this.api.loadSpread(designId, spreadId);
        await this.api.updateSpread(designId, spreadId, { model: { ...model, ignoredChecks: edit(model.ignoredChecks ?? []) } });
      });
    this.ignoreTails.set(spreadId, write);
    return write;
  }

  async setSpreadKind(designId: string, spreadId: string, kind: SpreadKindPick): Promise<UploadedTemplate> {
    if (kind === 'cover' || kind === 'vignette') {
      const category = kind === 'cover' ? DesignPackageSpreadCategoryEnum.COVER : DesignPackageSpreadCategoryEnum.VIGNETTES;
      return toTemplate(await this.api.updateSpread(designId, spreadId, { category }));
    }

    // PATCH replaces the whole model: the kind is applied to a fresh read, never to the tile's copy.
    // Lazy: this port is eager (app.config), the editor ops lib stays out of the initial bundle.
    const [{ model, category }, { applySpreadKind }] = await Promise.all([
      this.api.loadSpread(designId, spreadId),
      import('@lite-workspace/shared/design-document'),
    ]);
    const leavesCategory =
      category === DesignPackageSpreadCategoryEnum.COVER || category === DesignPackageSpreadCategoryEnum.VIGNETTES;
    const updated = await this.api.updateSpread(designId, spreadId, {
      ...(leavesCategory && { category: DesignPackageSpreadCategoryEnum.DEFAULT }),
      model: applySpreadKind(model, kind),
    });
    return toTemplate(updated);
  }

  async listPackSpreads(designId: string): Promise<PackSpreadRef[]> {
    const spreads = await this.api.loadSpreads(designId);
    return spreads.map(spread => ({ id: spread.id, name: spread.name ?? '', template: isPhotographerSpread(spread) }));
  }

  // The PATCH under the target's path is the move.
  async moveSpread(targetId: string, spreadId: string, name?: string): Promise<void> {
    await this.api.updateSpread(targetId, spreadId, name ? { name } : {});
  }

  async addTemplateFiles(designId: string, files: File[]): Promise<UploadedTemplate[]> {
    // Concurrency is bounded by the store's per-file fan-out (IMPORT_CONCURRENCY there).
    const created = await Promise.all(files.map(file => this.psdUpload.importPsd(designId, file)));
    return created.map(toUploadedTemplate);
  }

  /** Parse outcomes AND the render that follows them: the preview is a second server job with no event of
   *  its own, so the same watch owns both — a tile the parse left blank still fills in without a reload. */
  watchTemplatesParsing(designId: string, templateIds: string[]): Observable<TemplateParseUpdate> {
    const parses$ = this.parseWatcher.watch(designId, templateIds).pipe(
      map(parseResultToUpdate),
      mergeMap((update): Observable<TemplateParseUpdate> => {
        // An INDD placeholder hands its spot to one spread per artboard: those are the tiles that need a
        // render now, and the batch this watch started from has never heard of them.
        if (update.kind === 'replaced' && update.replacedBy.length > 0) {
          return concat(of(update), this.watchTemplateRenders(designId, update.replacedBy));
        }

        return of(update);
      }),
    );

    return merge(parses$, this.watchTemplateRenders(designId, templateIds));
  }

  /** The render alone. A settled tile has no parse left to watch, and re-watching it would settle a
   *  second time off the boxes it already has — a parse outcome for a parse that never re-ran. */
  watchTemplateRenders(designId: string, spreadIds: readonly string[]): Observable<TemplateParseUpdate> {
    return this.previewWatcher
      .watch(designId, spreadIds)
      .pipe(map((spread): TemplateParseUpdate => ({ kind: 'preview', patch: previewPatch(spread) })));
  }

  async reparseSpread(designId: string, spreadId: string): Promise<void> {
    await this.api.reparseSpread(designId, spreadId);
  }

  async overwriteTemplateFile(designId: string, templateId: string, file: File): Promise<UploadedTemplate> {
    const replaced = await this.psdUpload.replacePsd(designId, templateId, file);
    return toUploadedTemplate(replaced);
  }

  /**
   * The fonts the design REQUIRES come from the parsed spreads (`model.boxes[].params`), matched against
   * the fonts available for the design (`/api/files/fonts/for-designs`, uploaded + public) and the
   * persisted replacements (`/api/font-mappings/for-designs`) — so the list is complete the moment
   * step 2 opens and every resolution survives a refresh.
   */
  async getFontRows(designId: string, options?: { explicit?: boolean }): Promise<FontResolveRow[]> {
    if (options?.explicit) {
      this.fontsTable.clearFailuresForDesigns([designId]);
      this.fontMapping.clearFailuresForDesigns([designId]);
    }

    const [allSpreads, fonts] = await Promise.all([
      this.api.loadSpreads(designId),
      this.fontsTable.resolveForDesigns([designId]),
    ]);
    const spreads = photographerSpreads(allSpreads);
    const required = requiredFonts(spreads);
    // Mappings are scoped by the spreads' font names — the unscoped list is capped server-side and
    // silently drops the newest mappings, so a persisted replacement would show as «missing».
    // Each face's weighted name rides along: the canvas draws a face from it ahead of the bare name.
    const mappings = await this.fontMapping.resolveForDesigns(
      [designId],
      [
        ...required.map(({ name }) => name),
        ...required.flatMap(({ faces }) => faces.map(face => templateFontMappingKeys(face.name, face.weight, !!face.italic)[0])),
      ],
    );
    const presentByKey = new Map(
      fonts.flatMap(font =>
        [font.postScriptName, font.fontFamily, font.fullName]
          .filter((name): name is string => !!name)
          .map(name => [fontKey(name), font] as const),
      ),
    );
    const fontByFileId = new Map(fonts.map(font => [font.fileId, font]));
    const mappingByName = new Map(mappings.map(mapping => [fontKey(mapping.templateFontName), mapping]));
    const hasFile = (mapping: { fileId: string }): boolean => fontByFileId.has(mapping.fileId);
    // A replacement from the wizard lands on the row of the owner the server resolves for the write — the caller, or the
    // design's owner for a reviewing super-admin, whose own bare row moves onto that owner; an older gateway reports none,
    // so the caller stands in. A design row carries no scope, so the one-owner rule judges it.
    const signedInUserId = readAuthUserId();
    const writerId = this.fontMapping.writeOwnerForDesigns([designId]) ?? signedInUserId;
    const writer = writerId ? { userId: writerId } : null;

    return required.map(({ name, needsCyrillic, faces }) => {
      const drawnByWeightedMapping =
        faces.some(face => isFaceKeptByWeightedMapping(face, mappings, hasFile, writer, signedInUserId)) || undefined;
      const presentFont = presentByKey.get(fontKey(name));
      if (presentFont) {
        return { name, needsCyrillic, drawnByWeightedMapping, status: 'present' as const, fontUrl: presentFont.fontUrl };
      }

      const mapping = mappingByName.get(fontKey(name));
      if (mapping) {
        const mappedFont = fontByFileId.get(mapping.fileId);
        const replacedWith = mapping.fontMetrics?.family ?? mappedFont?.fontFamily ?? mapping.targetPostScriptName ?? '';
        // The face is `targetPostScriptName` alone — the value the client itself writes on every mapping
        // mutation — so an optimistic row and this read-back carry the same identity and compare equal.
        return {
          name,
          needsCyrillic,
          drawnByWeightedMapping,
          status: 'replaced' as const,
          replacedWith,
          replacedFace: mapping.targetPostScriptName,
          fontUrl: mappedFont?.fontUrl,
        };
      }

      return { name, needsCyrillic, drawnByWeightedMapping, status: 'missing' as const };
    });
  }

  invalidateFontCaches(designId: string): void {
    this.fontsTable.clearCacheForDesigns([designId]);
    // The mapping cache is keyed by designs AND by the name-set asked for, and a warm name index answers
    // later reads without any request — a per-design drop cannot reach those, so the whole cache goes.
    this.fontMapping.clearCache();
  }

  async getFontPickOptions(designId: string): Promise<FontPickOption[]> {
    const fonts = await this.fontsTable.resolveForDesigns([designId]);
    const byFamily = new Map<string, FontPickOption>();
    for (const font of fonts) {
      if (!font.fontFamily || font.removedFromLibrary) continue;
      const option = byFamily.get(font.fontFamily) ?? { name: font.fontFamily, fontUrl: font.fontUrl };
      const own = font.ownedByCaller === true ? [...(option.userFileIds ?? []), font.fileId] : option.userFileIds;
      byFamily.set(font.fontFamily, own ? { ...option, userFileIds: own } : option);
    }

    return [...byFamily.values()];
  }

  deleteUserFont(fileId: string): Promise<void> {
    return this.fontsTable.deleteUserFont(fileId);
  }

  async uploadFont(designId: string, rowName: string, file: File): Promise<void> {
    const uploaded = await this.fontUpload.uploadFont(designId, file);
    this.fontsTable.clearCache();
    // The mapping needs the FONT-TABLE file id — the raw upload id is a record `POST /api/font-mappings` rejects.
    const font = await this.findUploadedFont(designId, rowName, uploaded);
    if (!font) throw new Error(`uploadFont: uploaded font for ${rowName} did not appear in the fonts table`);
    await this.persistFontMapping(designId, rowName, font.fileId, font.postScriptName);
  }

  private async findUploadedFont(
    designId: string,
    rowName: string,
    uploaded: UploadedFont,
  ): Promise<FontApiResponse | undefined> {
    // Every weight of a face shares the family name, so matching on it hands back a sibling registered
    // earlier (Bold uploaded → Regular mapped); it is a last resort for a file that names nothing else.
    const identity = new Set([uploaded.postScriptName, uploaded.fullName].filter((name): name is string => !!name).map(fontKey));
    const wanted = identity.size > 0 ? identity : new Set(uploaded.names.map(fontKey));
    if (wanted.size === 0) return undefined;
    const candidateNames = (font: FontApiResponse): (string | undefined)[] =>
      identity.size > 0 ? [font.postScriptName, font.fullName] : [font.postScriptName, font.fullName, font.fontFamily];
    const matchesUpload = (font: FontApiResponse): boolean =>
      candidateNames(font)
        .filter((name): name is string => !!name)
        .some(name => wanted.has(fontKey(name)));
    // A variable font registers one row per instance under a single file id, and the file's own identity is
    // its first instance — the row asked for a named face, so its own name picks the instance.
    const namedLikeRow = (fonts: FontApiResponse[]): FontApiResponse | undefined =>
      fonts.find(font => [font.postScriptName, font.fullName].some(name => !!name && fontKey(name) === fontKey(rowName)));

    let readSucceeded = false;
    let lastReadError: unknown;
    // Registration lands over Kafka — on the platform budget, so a slow one is not reported as a failure.
    for (let attempt = 0; attempt < FONT_REGISTRATION_POLL_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, FONT_REGISTRATION_POLL_DELAY_MS));
        this.fontsTable.clearCache();
      }

      let fonts: FontApiResponse[];
      try {
        fonts = await this.fontsTable.resolveForDesigns([designId]);
      } catch (error) {
        // The face may still be registering — one flaky read must not burn the whole budget.
        lastReadError = error;
        continue;
      }

      readSucceeded = true;
      // Names are not unique across files, so a face registered earlier under the same name would win the
      // race against the file just uploaded; that file, when it is here, is the only honest answer.
      const ofUpload = uploaded.fileId ? fonts.filter(font => font.fileId === uploaded.fileId) : [];
      if (ofUpload.length > 0) return namedLikeRow(ofUpload) ?? ofUpload.find(matchesUpload) ?? ofUpload[0];

      const font = fonts.find(matchesUpload);
      if (font) return namedLikeRow(fonts.filter(sibling => sibling.fileId === font.fileId)) ?? font;
    }

    if (!readSucceeded && lastReadError) throw lastReadError;
    return undefined;
  }

  async replaceFont(designId: string, rowName: string, familyName: string): Promise<FontMatchResult> {
    const fonts = await this.fontsTable.resolveForDesigns([designId]);
    const font = fonts.find(candidate => candidate.fontFamily === familyName && !candidate.removedFromLibrary);
    if (!font) throw new Error(`replaceFont: no available font named ${familyName}`);
    await this.persistFontMapping(designId, rowName, font.fileId, font.postScriptName);
    return { family: familyName, fontUrl: font.fontUrl, face: font.postScriptName };
  }

  async searchFontCatalog(query: string): Promise<CatalogFontOption[]> {
    return this.fontAutoResolve.searchCatalog(query);
  }

  async replaceFontFromCatalog(designId: string, rowName: string, catalogName: string): Promise<FontMatchResult> {
    const resolved = await this.fontAutoResolve.resolveReplacement(rowName, catalogName);
    if (!resolved) throw new Error(`replaceFontFromCatalog: ${catalogName} could not be resolved`);
    this.fontsTable.clearCache();
    await this.persistFontMapping(designId, rowName, resolved.fileId, resolved.postScriptName);
    return { family: resolved.fontFamily, fontUrl: resolved.fontUrl, face: resolved.postScriptName };
  }

  private async persistFontMapping(
    designId: string,
    rowName: string,
    fileId: string,
    targetPostScriptName?: string,
  ): Promise<void> {
    // Scoped by the row name — the unscoped /for-designs list is capped server-side, and a mapping
    // dropped by the cap would be re-created as a duplicate instead of updated.
    // Best-effort: this read only picks create-vs-update; find-or-create below absorbs a check that could not answer.
    const mappings = await this.fontMapping.resolveForDesigns([designId], [rowName]).catch(() => []);
    const existing = mappings.find(mapping => fontKey(mapping.templateFontName) === fontKey(rowName));
    if (existing?.id) {
      await this.fontMapping.updateMapping(existing.id, { fileId, targetPostScriptName }, { designId });
      return;
    }

    // The design goes along so the gateway can write the row onto the pack owner on review and admit the owner's fonts.
    // `POST /api/font-mappings` is find-or-create per (templateFontName, user) and answers with the existing
    // row untouched — so a read that missed it (a failed fetch resolves to an empty list) keeps the old file.
    const created = await this.fontMapping.createMapping(
      { templateFontName: rowName, fileId, targetPostScriptName },
      { designId },
    );
    // The instances of one variable font share a file id, so the file alone does not say the row kept its face.
    const staleFace =
      !!targetPostScriptName && !!created?.targetPostScriptName && created.targetPostScriptName !== targetPostScriptName;
    if (created?.id && (created.fileId !== fileId || staleFace)) {
      await this.fontMapping.updateMapping(created.id, { fileId, targetPostScriptName }, { designId });
    }
  }

  // The handoff to the designer: it moves the pack out of draft, alerts the admin and mails the photographer.
  // Only the step-2 «на проверку» transition may call it — re-submitting from the review step would knock an
  // already-approved pack back into the queue.
  async submitForReview(designId: string): Promise<void> {
    await this.api.submitDesignPackage(designId);
  }

  // The pack's own status is the verdict — a spread's state tracks the parse/review pipeline, not the pack.
  async getReviewStatus(designId: string): Promise<ReviewStatus> {
    const pkg = await this.api.loadDesignPackage(designId);
    return reviewStatusOf(pkg.status);
  }

  async loadUpdates(copyId: string): Promise<TemplateUpdatesView> {
    const dto = await this.api.loadUpdates(copyId);
    return {
      studioName: dto.studioName ?? '',
      packName: dto.packName,
      newest: dto.newest,
      items: dto.items
        .map(item => ({
          id: item.sourceSpreadId,
          name: item.name ?? '',
          previewUrl: previewVersionsSrc(item.previewVersions) ?? '',
          kind: item.kind,
          isNew: item.isNew,
          ownerEdited: item.ownerEdited,
          publishedAt: item.publishedAt,
        }))
        .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)),
    };
  }

  syncUpdates(copyId: string, sourceSpreadIds: readonly string[]): Promise<{ synced: number; spreadsCount: number }> {
    return this.api.syncUpdates(copyId, sourceSpreadIds);
  }
}

/** Pack lifecycle → app-native verdict. An unknown status reads as `pending` — with the designer, never
 *  a draft: a false `idle` would re-arm the fonts CTA's first submit and re-queue a reviewed pack. */
function reviewStatusOf(status: DesignPackage['status']): ReviewStatus {
  if (status === DesignStatus.DRAFT) return 'idle';
  if (status === DesignStatus.APPROVED) return 'approved';
  if (status === DesignStatus.CHANGES_REQUESTED) return 'changes';
  return 'pending';
}

/** Pack lifecycle → the surface-facing status. An unknown status stays `review`, never `draft`: telling
 *  the photographer to go finish a pack that is actually queued would have them re-submit it. */
function designPackStatusOf(pkg: DesignPackage): DesignPackStatus {
  if (pkg.status === DesignStatus.APPROVED) return 'ready';
  if (pkg.status === DesignStatus.DRAFT) return 'draft';
  if (pkg.status === DesignStatus.CHANGES_REQUESTED) return 'changes';
  return 'review';
}

// The list serializer sends the studio FK as `partnerStudio`, not `partnerStudioId`.
function toSummary(pkg: DesignPackage & { partnerStudio?: unknown }): DesignSummary {
  return {
    id: pkg.id,
    name: pkg.name,
    createdAt: pkg.createdAt ? new Date(pkg.createdAt).toISOString() : '',
    approved: pkg.status === DesignStatus.APPROVED,
    // An unknown status is treated as reviewed, never as residue — the grid may not hide what it cannot classify.
    isDraft: pkg.status === DesignStatus.DRAFT,
    changesRequested: pkg.status === DesignStatus.CHANGES_REQUESTED,
    status: designPackStatusOf(pkg),
    // Cover + count are baked server-side onto the list item; previewVersions is the pack's own preview fallback.
    templatesCount: pkg.spreadsCount ?? 0,
    coverUrl: pkg.coverUrl ?? pkg.previewVersions?.thumb?.path ?? undefined,
    coverWidth: pkg.coverWidth ?? sizedPreview(pkg)?.width,
    coverHeight: pkg.coverHeight ?? sizedPreview(pkg)?.height,
    // No preview fallback on purpose: the preview describes whichever spread got rendered, and letting it
    // stand in for the template is exactly how the two ends came to disagree.
    templateWidth: pkg.templateWidth,
    templateHeight: pkg.templateHeight,
    sizeLabel: pkg.sizeLabel ?? null,
    printProductId: pkg.printSettings?.printProductId ?? null,
    sourcePackageId: pkg.sourcePackageId,
    pendingUpdates: pkg.pendingUpdates ?? 0,
    hasUnseenUpdates: pkg.hasUnseenUpdates ?? false,
    own: pkg.partnerStudio == null && pkg.partnerStudioId == null && pkg.audience == null && !pkg.sourcePackageId,
    problemsCount: pkg.problemsCount ?? 0,
  };
}

// The baked preview's dimensions speak for the pack's spread ratio whichever cover URL renders.
function sizedPreview(pkg: DesignPackage): { width: number; height: number } | undefined {
  const preview = pkg.previewVersions?.thumb ?? pkg.previewVersions?.medium;
  return preview?.width && preview?.height ? preview : undefined;
}

// Backgrounds are extracted from the uploaded spreads for the designer to moderate; the photographer's
// surface never lists them — they reach the album only as the backdrop under a vignette or collage.
// A saved vignette variant is a roster of the pack's vignette, not a spread of the pack (R309).
function isPhotographerSpread(spread: DesignPackageSpread): boolean {
  return spread.category !== DesignPackageSpreadCategoryEnum.BACKGROUND && !spread.vignetteVersion;
}

function photographerSpreads(spreads: DesignPackageSpread[]): DesignPackageSpread[] {
  return spreads.filter(isPhotographerSpread);
}

function toTemplate(spread: DesignPackageSpread): UploadedTemplate {
  const state = spreadRenderState(spread);
  return {
    id: spread.id,
    name: spread.name,
    state,
    // The spread carries no fail-reason column; 'system' keeps the retry offer visible after a reload —
    // a genuinely broken file re-dies fast with the honest kind over WS (spec R3.3).
    ...(state === 'error' ? { errorKind: 'system' as const } : {}),
    type: templateTypeFromCategory(spread.category),
    previewSrc: spreadPreviewSrc(spread),
    previewThumbSrc: spreadThumbSrc(spread),
    previewLargeSrc: spreadLargeSrc(spread),
    previewThumbHash: spread.model?.previewVersions?.thumbHash || undefined,
    previewWidth: spreadPreviewSize(spread)?.width,
    previewHeight: spreadPreviewSize(spread)?.height,
    width: spread.model?.originalWidth,
    height: spread.model?.originalHeight,
    guides: spread.model?.authoredGuides?.vertical,
    updatedAt: spread.updatedAt,
    reviewed: spread.reviewMarker === true,
    kind: spreadKindOf(spread.model, spread.category),
    // A spread still without boxes is mid-parse: judged now it would read as «no frames».
    ...(state === 'ready' || state === 'parsing' ? { checks: spread } : {}),
  };
}

/** A freshly created (or WS/poll-refreshed) spread → uploaded-template; previews come from the server parse. */
function toUploadedTemplate(created: CreatedSpread): UploadedTemplate {
  return toTemplate(created.spread);
}

/** A parse outcome → a keyed patch the store merges (ready with server preview + boxes, or error). */
function parseResultToUpdate(result: SpreadParseResult): TemplateParseUpdate {
  // A replaced placeholder is not a state change on a tile — the tile itself is gone and others took over.
  if (result.replaced) {
    return { kind: 'replaced', sourceId: result.spreadId, replacedBy: result.replacedBy ?? [] };
  }

  return { kind: 'patch', patch: parseResultToPatch(result) };
}

function parseResultToPatch(result: SpreadParseResult): TemplatePatch {
  if (result.spread && !result.failed) {
    const template = toTemplate(result.spread);
    // No `type`: the parse job never writes the spread's category, so this read can only repeat what the
    // server knew when it ran — and a pick still on the wire at that moment would be stomped back to ''.
    // Raw size + guides ride the parse: a tile born before it has none, and step 3's size check reads them.
    const { width, height, guides, checks } = template;
    const sized = width && height ? { width, height, guides } : {};
    return withSentPreviewFields({ id: template.id, state: template.state, ...sized, ...(checks ? { checks } : {}) }, template);
  }

  return { id: result.spreadId, state: 'error', errorKind: result.errorKind ?? 'system' };
}

/** The render fields the server actually sent, and nothing else: a preview arriving late must neither
 *  speak for state, type or name — all of which the user may have changed in the minutes since the parse
 *  settled this tile — nor blank a tier this render happens not to carry. */
function previewPatch(spread: DesignPackageSpread): TemplatePatch {
  const template = toTemplate(spread);
  return withSentPreviewFields({ id: template.id }, template);
}

/** Server previews lag the parse — only patch the fields the server already sent, so an existing tile
 *  preview is never blanked out, and refresh both render tiers together so the filmstrip thumb never
 *  trails the full-size preview. ONE picker for the parse patch and the render patch: were the two to
 *  drift, the tiers a tile shows would depend on which job's read got home last. */
function withSentPreviewFields(base: TemplatePatch, template: UploadedTemplate): TemplatePatch {
  let patch = base;
  if (template.previewSrc) patch = { ...patch, previewSrc: template.previewSrc };
  if (template.previewThumbSrc) patch = { ...patch, previewThumbSrc: template.previewThumbSrc };
  if (template.previewLargeSrc) patch = { ...patch, previewLargeSrc: template.previewLargeSrc };
  if (template.previewThumbHash) patch = { ...patch, previewThumbHash: template.previewThumbHash };
  if (template.previewWidth && template.previewHeight)
    patch = { ...patch, previewWidth: template.previewWidth, previewHeight: template.previewHeight };
  return patch;
}

/** A spread with no parsed boxes yet is still processing even if the backend state says otherwise. */
function spreadRenderState(spread: DesignPackageSpread): UploadedTemplate['state'] {
  const mapped = uploadedFileStateFromSpread(spread.state);
  if (mapped === 'error') return 'error';
  const hasBoxes = Array.isArray(spread.model?.boxes) && spread.model.boxes.length > 0;
  if (!hasBoxes) return 'processing';
  // Рендер превью — второй серверный job после разбора: пока он не дошёл, плитка честно
  // показывает, что работа продолжается, вместо пустого места под видом готовой.
  return spreadThumbSrc(spread) || spreadPreviewSrc(spread) ? 'ready' : 'parsing';
}

/** Every distinct font the parsed spreads reference (`visitSpreadFonts`), with `needsCyrillic` = any
 *  text-box using it holds Cyrillic. Deduped case-insensitively — different PSDs spell the same face
 *  differently (SemiBold/Semibold); a font missed here never reaches the gate. */
function requiredFonts(spreads: DesignPackageSpread[]): { name: string; needsCyrillic: boolean; faces: TemplateFontFace[] }[] {
  const byKey = new Map<string, { name: string; needsCyrillic: boolean; faces: TemplateFontFace[] }>();
  const add = (face: TemplateFontFace, text: string | undefined): void => {
    const key = fontKey(face.name);
    const cyrillic = hasCyrillicText(text);
    const existing = byKey.get(key);
    if (existing) {
      const faces = existing.faces.some(f => f.weight === face.weight && f.italic === face.italic)
        ? existing.faces
        : [...existing.faces, face];
      byKey.set(key, { ...existing, needsCyrillic: existing.needsCyrillic || cyrillic, faces });
    } else {
      byKey.set(key, { name: face.name, needsCyrillic: cyrillic, faces: [face] });
    }
  };

  for (const spread of spreads) visitSpreadFonts(spread.model?.boxes, add);
  return [...byKey.values()];
}

function fontKey(name: string): string {
  return name.trim().toLowerCase();
}
